/**
 * Component that enforces default query parameters defined in route configuration
 * Automatically appends missing default params when routes are accessed directly
 */
import { RouteMapContext } from "./RouteMapContext";
import { matchPathToRouteName } from "./routeHelpers";
import React, { memo, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface EnforceDefaultQueryParamsProps {
  children: React.ReactNode;
}

/**
 * Ensures all routes have their default query parameters present in the URL
 * When a user navigates directly to a URL (bypassing programmatic navigation),
 * this component checks if default params are missing and adds them transparently
 *
 * @example
 * // Route config: { name: "pages", path: "/sections", defaultParams: { siteType: "onlineInvitation" } }
 * // User types: /site/123/sections
 * // Normalized to: /site/123/sections?siteType=onlineInvitation
 */
export const EnforceDefaultQueryParams: React.FC<
  EnforceDefaultQueryParamsProps
> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { defaultParamsMap, pathMap } = useContext(RouteMapContext);

  useEffect(() => {
    // Skip root path - AppContainer handles initial routing
    if (location.pathname === "/") {
      return;
    }

    const matched = matchPathToRouteName(location.pathname, pathMap);

    if (!matched) {
      return;
    }

    const defaultParams = defaultParamsMap.get(matched.name);
    if (!defaultParams || Object.keys(defaultParams).length === 0) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    let hasMissingParams = false;
    const paramsToAdd: Record<string, string> = {};

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!searchParams.has(key)) {
        hasMissingParams = true;
        paramsToAdd[key] = String(value);
      }
    });

    if (hasMissingParams) {
      Object.entries(paramsToAdd).forEach(([key, value]) => {
        searchParams.set(key, value);
      });

      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

      // Use replace to avoid polluting browser history
      navigate(newUrl, { replace: true });
    }
  }, [defaultParamsMap, location.pathname, location.search, navigate, pathMap]);

  return <>{children}</>;
};

export default memo(EnforceDefaultQueryParams);
